import { useEffect, useMemo } from 'react';
import { Checkbox } from '@/components/Checkbox';
import { Select } from '@/components/Select';
import { Button } from '@/components/Button';
import { useAuth } from '@/auth/AuthProvider';
import { cx } from '@/lib/cx';
import { useBusinessOperators } from '../api';
import '../masters.css';

interface BaseProps {
  /** 事業年度（年度別の表示順で並べる） */
  fiscalYear: number;
  disabled?: boolean;
  invalid?: boolean;
  id?: string;
  /** 単一選択の未選択表示（既定「すべて」）。null/'' で非表示 */
  placeholder?: string | null;
  width?: 'xs' | 'sm' | 'md' | 'lg' | 'full';
  /** 廃止（無効）の事業実施者も含める */
  includeInactive?: boolean;
  className?: string;
  'aria-label'?: string;
  'aria-describedby'?: string;
}

export type BusinessOperatorSelectProps =
  | (BaseProps & { multiple?: false; value: number | null; onChange: (value: number | null) => void })
  | (BaseProps & { multiple: true; value: number[]; onChange: (value: number[]) => void });

/**
 * 事業実施者の選択部品（03_作業分担 §6.3）。
 * - 漁協ユーザーは自組織に固定され、変更できない（API も自組織のみを返す）。
 * - `multiple` を付けるとチェックボックス一覧（全選択・全解除つき）になる。
 */
export function BusinessOperatorSelect(props: BusinessOperatorSelectProps) {
  const { fiscalYear, disabled, invalid, id, width = 'full', includeInactive, className } = props;
  const { user } = useAuth();
  const ownId = user?.organizationType === '漁協' && user.businessOperatorId != null ? Number(user.businessOperatorId) : null;
  const { data, isPending } = useBusinessOperators(fiscalYear, { includeInactive });
  const options = useMemo(() => data ?? [], [data]);

  // 漁協は自組織に固定（親の状態も自組織へ揃える）
  const single = !props.multiple;
  const currentSingle = single ? (props.value as number | null) : null;
  const currentMulti = !single ? (props.value as number[]) : [];
  const onChangeProp = props.onChange;
  const multiple = !!props.multiple;
  useEffect(() => {
    if (ownId == null) return;
    if (!multiple) {
      if (currentSingle !== ownId) (onChangeProp as (v: number | null) => void)(ownId);
    } else if (currentMulti.length !== 1 || currentMulti[0] !== ownId) {
      (onChangeProp as (v: number[]) => void)([ownId]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownId, multiple, currentSingle, currentMulti.join(',')]);

  if (!props.multiple) {
    const placeholder = props.placeholder === undefined ? 'すべて' : (props.placeholder ?? undefined);
    return (
      <Select
        id={id}
        className={className}
        width={width}
        invalid={invalid}
        aria-label={props['aria-label'] ?? '事業実施者'}
        aria-describedby={props['aria-describedby']}
        disabled={disabled || ownId != null}
        placeholder={ownId != null ? undefined : placeholder}
        value={props.value ?? ''}
        onChange={(e) => props.onChange(e.target.value === '' ? null : Number(e.target.value))}
        options={options.map((o) => ({ value: o.id, label: o.isActive ? o.name : `${o.name}（廃止）` }))}
        data-loading={isPending || undefined}
      />
    );
  }

  const selected = new Set(props.value);
  const toggle = (opId: number, on: boolean) => {
    const next = new Set(selected);
    if (on) next.add(opId);
    else next.delete(opId);
    props.onChange(options.map((o) => o.id).filter((x) => next.has(x)));
  };
  const lock = disabled || ownId != null;
  return (
    <div
      id={id}
      role="group"
      aria-label={props['aria-label'] ?? '事業実施者'}
      aria-describedby={props['aria-describedby']}
      className={cx('bs-opsel', invalid && 'is-invalid', className)}
    >
      <div className="bs-opsel__head">
        <span className="bs-opsel__count">{selected.size} / {options.length} 件選択</span>
        {!lock && (
          <>
            <Button size="sm" onClick={() => props.onChange(options.map((o) => o.id))}>全選択</Button>
            <Button size="sm" onClick={() => props.onChange([])}>全解除</Button>
          </>
        )}
      </div>
      <div className="bs-opsel__list">
        {options.map((o) => (
          <Checkbox
            key={o.id}
            label={o.isActive ? o.name : `${o.name}（廃止）`}
            checked={selected.has(o.id)}
            disabled={lock}
            onChange={(e) => toggle(o.id, e.target.checked)}
          />
        ))}
        {!isPending && options.length === 0 && <span className="bs-combo__empty">事業実施者がありません</span>}
      </div>
    </div>
  );
}
